import { useState } from "react";
import { ArrowRight, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useAIPanel } from "@/contexts/AIPanelContext";
import { buildCrmClientWorkflowStages } from "@/config/workflowConfig";

type PipelineStage = {
  label: string;
  value: number;
  href: string;
  bg?: string;
  color?: string;
  borderColor?: string;
  items?: Array<{ title: string; client?: string }>;
};

const NAVY = "#1B3A5C";

export type CommercialPipelineData = {
  stagesBefore?: PipelineStage[];
  parallelStages?: PipelineStage[];
  stagesAfter?: PipelineStage[];
  stages?: PipelineStage[];
  finalStages?: PipelineStage[];
};

function truncate(str: string, n = 38): string {
  return str.length > n ? str.slice(0, n) + "…" : str;
}

/** Tooltip mini-list that appears on hover */
function StageTooltip({ stage, visible }: { stage: PipelineStage; visible: boolean }) {
  if (!visible) return null;
  const items = stage.items ?? [];

  return (
    <div
      className="absolute z-50 bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 w-56 rounded-xl border border-slate-200 bg-white shadow-xl p-3 pointer-events-none"
      style={{ boxShadow: "0 8px 32px rgba(13,46,69,0.14)" }}
    >
      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">
        {stage.label} · {stage.value}
      </p>
      {items.length === 0 ? (
        <p className="text-[11px] text-slate-400 italic">Sem registros no momento.</p>
      ) : (
        <ul className="space-y-1">
          {items.slice(0, 5).map((item, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span
                className="mt-[3px] w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: stage.color || NAVY }}
              />
              <span className="text-[11px] text-slate-700 leading-tight">{truncate(item.title)}</span>
            </li>
          ))}
          {items.length > 5 && (
            <li className="text-[10px] text-slate-400 pl-3">+{items.length - 5} mais</li>
          )}
        </ul>
      )}
      <p className="mt-2 text-[9px] text-blue-500 font-bold">Clique para consultar a IA ›</p>
      {/* Caret */}
      <div
        className="absolute left-1/2 -translate-x-1/2 bottom-[-6px] w-3 h-3 rotate-45 border-r border-b border-slate-200 bg-white"
      />
    </div>
  );
}

function StageBlock({
  stage,
  small,
  isEmpty,
}: {
  stage: PipelineStage;
  small?: boolean;
  isEmpty?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const { openPanel } = useAIPanel();

  const handleClick = (e: React.MouseEvent) => {
    if (isEmpty) {
      e.preventDefault();
      openPanel(`Me mostre os atendimentos em "${stage.label}" do pipeline comercial`);
    }
  };

  return (
    <div className="relative flex-1" style={{ minWidth: 0 }}>
      <StageTooltip stage={stage} visible={hovered} />
      <Link
        to={stage.href}
        onClick={handleClick}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="group flex items-center justify-between border px-2.5 transition-all"
        style={{
          background: stage.bg,
          color: stage.color,
          borderColor: stage.borderColor,
          borderStyle: "solid",
          minWidth: 0,
          height: small ? 36 : 56,
          borderRadius: 12,
          boxShadow: "0 1px 2px rgba(13,46,69,0.03)",
          transition: "all 0.18s cubic-bezier(0.2,0.8,0.2,1)",
          opacity: stage.value === 0 ? 0.72 : 1,
        }}
      >
        <span
          className="text-[10px] font-semibold leading-tight"
          style={{ maxWidth: "65%", opacity: stage.value === 0 ? 0.72 : 0.82 }}
        >
          {stage.label}
        </span>
        <span
          className="text-base font-bold leading-none ml-2"
          style={{ opacity: stage.value === 0 ? 0.82 : 1 }}
        >
          {stage.value}
        </span>
      </Link>
    </div>
  );
}

function Arrow() {
  return (
    <div className="flex flex-shrink-0 items-center justify-center self-center">
      <ChevronRight className="h-3.5 w-3.5 text-slate-400/40" />
    </div>
  );
}

function buildClientFlowStages(raw: CommercialPipelineData | any): PipelineStage[] {
  return buildCrmClientWorkflowStages(raw ?? {}) as PipelineStage[];
}

export function CommercialPipeline({ data }: { data?: CommercialPipelineData | any }) {
  const stages = buildClientFlowStages(data ?? {});
  const totalValue = stages.reduce((sum, stage) => sum + Number(stage.value || 0), 0);
  const isEmpty = totalValue === 0;

  return (
    <div
      className="rounded-2xl border bg-white px-4 py-3"
      style={{
        borderColor: "rgba(226,235,240,0.5)",
        boxShadow: "0 1px 3px rgba(13,46,69,0.04), 0 4px 12px rgba(13,46,69,0.02)",
        borderRadius: 16,
      }}
    >
      <div className="mb-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="flex h-5 w-5 items-center justify-center rounded-md" style={{ background: "rgba(27,58,92,0.1)" }}>
            <ArrowRight className="h-3 w-3" style={{ color: NAVY }} />
          </div>
          <h2 className="text-xs font-bold text-slate-800">NIP CRM</h2>
        </div>
        <Link
          to="/comercial"
          className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
        >
          Página Inicial CRM
        </Link>
        {isEmpty && (
          <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
            Aguardando atendimentos
          </span>
        )}
        {!isEmpty && (
          <span className="text-[10px] font-semibold text-slate-600">
            {totalValue} negócios ativos
          </span>
        )}
      </div>

      {isEmpty && (
        <p className="text-[11px] text-slate-400 mb-2.5 px-0.5">
          Atendimento, negociação e fechamento aparecerão aqui assim que o CRM receber novos atendimentos.
        </p>
      )}

      <div className="flex items-center gap-1.5 overflow-x-auto" style={{ flexWrap: "wrap" }}>
        {stages.map((stage, i) => (
          <div key={stage.label} className="flex flex-1 items-center gap-1.5" style={{ minWidth: 0 }}>
            {i > 0 && <Arrow />}
            <StageBlock stage={stage} isEmpty={isEmpty} />
          </div>
        ))}
      </div>
    </div>
  );
}
